import type {PricedItem} from "#pricing/types/pricing-types"
import {PriceList} from "#pricing/nodes/price-list"
import {PricedItemNode} from "#pricing/nodes/priced-item-node"
import {PricingNode, registerPricingGraphNode} from "#pricing/pricing-node"
import type {PricingContext} from "#pricing/pricing-context"
import type {Evaluator, GraphNodeDefinition} from "@cm/graph-v3"
import {TraversalAction, graphNode} from "@cm/graph-v3"
import {z} from "zod/v4-mini"

export type FlattenPriceListParameters = {subprices: PriceList}

const flattenPriceListDefinition = {
    nodeClass: "FlattenPriceList",
    validation: {
        parameters: z.object({subprices: graphNode(PriceList)}),
        returns: z.array(z.custom<PricedItem>(() => true)),
    },
} satisfies GraphNodeDefinition<PricedItem[], FlattenPriceListParameters>

@registerPricingGraphNode
export class FlattenPriceList extends PricingNode<PricedItem[], FlattenPriceListParameters> {
    public override get definition() {
        return flattenPriceListDefinition
    }

    override run = async (evaluator: Evaluator<PricingContext>) => {
        const subprices = await evaluator.get(this.parameters.subprices)
        return subprices.flat()
    }

    getSubprices(): PriceList {
        return this.parameters.subprices
    }

    getNodesOfType<T>(type: new (...args: any[]) => T): T[] {
        const nodes = new Set<T>()
        this.depthFirstTraversalPreorder((node) => {
            if (node instanceof type) {
                nodes.add(node)
            }
            return TraversalAction.Continue
        })
        return Array.from(nodes)
    }

    getOriginalIdsFromCatalog(): string[] {
        const identifiers = new Set<string>()
        this.depthFirstTraversalPreorder((node) => {
            if (node instanceof PricedItemNode) {
                const identifier = node.getUniqueId()
                if (identifier.length === 0) throw new Error(`Node ${node} has no unique identifier`)
                identifiers.add(identifier)
            }
            return TraversalAction.Continue
        })

        return Array.from(identifiers)
    }
}
